/**
 * Directory results shell — Seznam (inset group / Favorites–Shops split) or Karty (card grid).
 */

import type { ReactNode } from 'react';
import type { DirectoryDensityMode } from './directoryDensity.js';
import {
  DIRECTORY_CARD_ITEM_CLASS,
  DIRECTORY_CARD_PAD_CLASS,
  DIRECTORY_CARDS_GRID_CLASS,
  DIRECTORY_FAVORITES_LIST_GRID_CLASS,
  DIRECTORY_FAVORITES_LIST_ITEM_CLASS,
  DIRECTORY_ORDERS_GRID_CLASS,
  DIRECTORY_ORG_PICKER_GRID_CLASS,
  DIRECTORY_PICKER_ROW_PAD_CLASS,
} from './directoryPickerLayout.js';
import { DirectoryPickerGroup } from './DirectoryPickerGroup.js';
import {
  DirectoryEntityRowSkeleton,
  type DirectoryEntityRowDensity,
} from './DirectoryEntityRow.js';
import type { DirectoryEntityThumbSize } from './DirectoryEntityThumb.js';

export interface DirectoryResultsProps {
  readonly density: DirectoryDensityMode;
  readonly children: ReactNode;
  readonly className?: string;
  readonly testId?: string;
  /**
   * Seznam layout. `inset` = single divide-y group (legacy).
   * `split2` = 1 → lg 2 → xl 3 (Favorites / Shops).
   * `orgPicker` = 1 → sm 2 → lg 3 (~33% on lg+) (Organizace).
   * `twoCol` = 1 → lg 2 → xl 3 (Objednávky — long copy + CTAs).
   */
  readonly listLayout?: 'inset' | 'split2' | 'orgPicker' | 'twoCol';
  /**
   * Karty grid. `dense` = lg+ 3-col (~33%) (Shops cards).
   * `split2` = same 1→lg2→xl3 Seznam split (Favorites-style).
   * `orgPicker` = 1 → sm 2 → lg 3 (~33% on lg+) (Organizace).
   * `twoCol` = same 1→lg2→xl3 as Objednávky Seznam.
   */
  readonly cardsLayout?: 'dense' | 'split2' | 'orgPicker' | 'twoCol';
  readonly 'aria-busy'?: boolean;
  readonly 'aria-live'?: 'polite' | 'assertive' | 'off';
}

/**
 * Wraps directory item `<li>` children.
 * - `list` + inset → DirectoryPickerGroup (divide-y)
 * - `list` + split2/orgPicker/twoCol → shared list grid tokens via `resolveDirectoryListGridClass`
 * - `cards` → shared Karty grid tokens via `resolveDirectoryCardsGridClass` (dense/orgPicker cap at lg 3-col; no xl 4-col)
 */
export function DirectoryResults(props: DirectoryResultsProps): JSX.Element {
  const {
    density,
    children,
    className,
    testId = 'directory-results',
    listLayout = 'inset',
    cardsLayout = 'dense',
  } = props;
  const ariaBusy = props['aria-busy'];
  const ariaLive = props['aria-live'];

  if (density === 'cards') {
    const cardsGridClass = resolveDirectoryCardsGridClass(cardsLayout);
    return (
      <ul
        className={[cardsGridClass, className].filter(Boolean).join(' ')}
        data-testid={testId}
        data-density="cards"
        {...(cardsLayout === 'split2' ? { 'data-cards-layout': 'split2' as const } : {})}
        {...(cardsLayout === 'orgPicker' ? { 'data-cards-layout': 'orgPicker' as const } : {})}
        {...(cardsLayout === 'twoCol' ? { 'data-cards-layout': 'twoCol' as const } : {})}
        {...(ariaBusy === true ? { 'aria-busy': true } : {})}
        {...(ariaLive !== undefined ? { 'aria-live': ariaLive } : {})}
      >
        {children}
      </ul>
    );
  }

  if (listLayout === 'split2' || listLayout === 'orgPicker' || listLayout === 'twoCol') {
    const listGridClass = resolveDirectoryListGridClass(listLayout);
    return (
      <ul
        className={[listGridClass, className].filter(Boolean).join(' ')}
        data-testid={testId}
        data-density="list"
        data-list-layout={listLayout}
        {...(ariaBusy === true ? { 'aria-busy': true } : {})}
        {...(ariaLive !== undefined ? { 'aria-live': ariaLive } : {})}
      >
        {children}
      </ul>
    );
  }

  return (
    <DirectoryPickerGroup
      className={className}
      testId={testId}
      aria-busy={ariaBusy}
      aria-live={ariaLive}
      data-density="list"
    >
      {children}
    </DirectoryPickerGroup>
  );
}

export interface DirectoryResultsSkeletonProps {
  readonly density: DirectoryDensityMode;
  readonly count?: number;
  readonly size?: DirectoryEntityThumbSize;
  readonly rowDensity?: DirectoryEntityRowDensity;
  readonly className?: string;
  readonly testId?: string;
  /** Match DirectoryResults Seznam layout (Favorites / Shops use split2; Orgs use orgPicker). */
  readonly listLayout?: 'inset' | 'split2' | 'orgPicker' | 'twoCol';
  /** Match DirectoryResults Karty grid (Orgs use orgPicker; Objednávky uses twoCol). */
  readonly cardsLayout?: 'dense' | 'split2' | 'orgPicker' | 'twoCol';
}

const SKELETON_KEYS = ['sk-dir-1', 'sk-dir-2', 'sk-dir-3', 'sk-dir-4', 'sk-dir-5', 'sk-dir-6'] as const;

function resolveDirectoryCardsGridClass(
  cardsLayout: 'dense' | 'split2' | 'orgPicker' | 'twoCol',
): string {
  if (cardsLayout === 'orgPicker') {
    return DIRECTORY_ORG_PICKER_GRID_CLASS;
  }
  if (cardsLayout === 'split2') {
    return DIRECTORY_FAVORITES_LIST_GRID_CLASS;
  }
  if (cardsLayout === 'twoCol') {
    return DIRECTORY_ORDERS_GRID_CLASS;
  }
  return DIRECTORY_CARDS_GRID_CLASS;
}

function resolveDirectoryListGridClass(
  listLayout: 'split2' | 'orgPicker' | 'twoCol',
): string {
  if (listLayout === 'orgPicker') {
    return DIRECTORY_ORG_PICKER_GRID_CLASS;
  }
  return listLayout === 'twoCol'
    ? DIRECTORY_ORDERS_GRID_CLASS
    : DIRECTORY_FAVORITES_LIST_GRID_CLASS;
}

/**
 * Loading placeholder matching Seznam group / Favorites–Shops split / Karty grid.
 */
export function DirectoryResultsSkeleton(
  props: DirectoryResultsSkeletonProps,
): JSX.Element {
  const {
    density,
    count = 3,
    size = 'lg',
    rowDensity = 'comfortable',
    className,
    testId = 'directory-results-skeleton',
    listLayout = 'inset',
    cardsLayout = 'dense',
  } = props;
  const keys = SKELETON_KEYS.slice(0, Math.min(Math.max(count, 1), SKELETON_KEYS.length));

  if (density === 'cards') {
    const cardsGridClass = resolveDirectoryCardsGridClass(cardsLayout);
    return (
      <ul
        className={[cardsGridClass, className].filter(Boolean).join(' ')}
        aria-busy="true"
        aria-live="polite"
        data-testid={testId}
        data-density="cards"
        {...(cardsLayout === 'split2' ? { 'data-cards-layout': 'split2' as const } : {})}
        {...(cardsLayout === 'orgPicker' ? { 'data-cards-layout': 'orgPicker' as const } : {})}
        {...(cardsLayout === 'twoCol' ? { 'data-cards-layout': 'twoCol' as const } : {})}
      >
        {keys.map((key) => (
          <li
            key={key}
            className={[DIRECTORY_CARD_ITEM_CLASS, DIRECTORY_CARD_PAD_CLASS].join(' ')}
            data-testid={`${testId}-${key}`}
          >
            <DirectoryEntityCardSkeleton
              size={size === 'md' ? 'xl' : size}
              density={rowDensity}
              testId={`${testId}-${key}-card`}
            />
          </li>
        ))}
      </ul>
    );
  }

  if (listLayout === 'split2' || listLayout === 'orgPicker' || listLayout === 'twoCol') {
    const listGridClass = resolveDirectoryListGridClass(listLayout);
    return (
      <ul
        className={[listGridClass, className].filter(Boolean).join(' ')}
        aria-busy="true"
        aria-live="polite"
        data-testid={testId}
        data-density="list"
        data-list-layout={listLayout}
      >
        {keys.map((key) => (
          <li
            key={key}
            className={DIRECTORY_FAVORITES_LIST_ITEM_CLASS}
            data-testid={`${testId}-${key}`}
          >
            <DirectoryEntityRowSkeleton
              size={size}
              density={rowDensity}
              testId={`${testId}-${key}-row`}
            />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <DirectoryPickerGroup
      aria-busy
      aria-live="polite"
      className={className}
      testId={testId}
    >
      {keys.map((key) => (
        <li key={key} className={DIRECTORY_PICKER_ROW_PAD_CLASS} data-testid={`${testId}-${key}`}>
          <DirectoryEntityRowSkeleton
            size={size}
            density={rowDensity}
            testId={`${testId}-${key}-row`}
          />
        </li>
      ))}
    </DirectoryPickerGroup>
  );
}

export interface DirectoryEntityCardSkeletonProps {
  readonly size?: DirectoryEntityThumbSize;
  readonly density?: DirectoryEntityRowDensity;
  readonly className?: string;
  readonly testId?: string;
}

/** Stacked Karty skeleton — thumb block + two text lines. */
export function DirectoryEntityCardSkeleton(
  props: DirectoryEntityCardSkeletonProps = {},
): JSX.Element {
  const {
    size = 'xl',
    density = 'comfortable',
    className,
    testId = 'directory-entity-card-skeleton',
  } = props;

  let mediaSizeClass =
    'h-[var(--directory-thumb-size)] w-[var(--directory-thumb-size)] rounded-[var(--directory-thumb-radius)]';
  if (size === 'xl') {
    mediaSizeClass =
      'h-[var(--directory-thumb-size-xl)] w-[var(--directory-thumb-size-xl)] rounded-[var(--directory-thumb-radius-xl)]';
  } else if (size === 'lg') {
    mediaSizeClass =
      'h-[var(--directory-thumb-size-lg)] w-[var(--directory-thumb-size-lg)] rounded-[var(--directory-thumb-radius-lg)]';
  }

  return (
    <div
      className={['flex flex-col gap-3', className].filter(Boolean).join(' ')}
      aria-busy="true"
      aria-live="polite"
      data-testid={testId}
      data-density={density}
      data-layout="stack"
    >
      <span
        className={[
          'inline-block shrink-0 animate-pulse',
          mediaSizeClass,
          'bg-[var(--directory-thumb-skeleton)]',
          'motion-reduce:animate-none',
        ].join(' ')}
        data-testid={`${testId}-media`}
      />
      <div className="flex min-w-0 flex-col gap-2">
        <span
          className="h-3.5 w-3/4 max-w-[14rem] animate-pulse rounded-md bg-[var(--directory-thumb-skeleton)] motion-reduce:animate-none"
          data-testid={`${testId}-line-1`}
        />
        <span
          className="h-3 w-1/2 max-w-[10rem] animate-pulse rounded-md bg-[var(--directory-thumb-skeleton)] motion-reduce:animate-none"
          data-testid={`${testId}-line-2`}
        />
      </div>
    </div>
  );
}
